<template>
  <div class="nps-app-root min-h-screen flex flex-col justify-between">
    <!-- Header Minimalista e Sofisticado -->
    <header class="nps-header py-3.5 px-4 sm:px-6 bg-white/95 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-30">
      <div class="max-w-2xl mx-auto flex items-center justify-between">
        <!-- Logo Oficial (sem repetição excessiva de nomes) -->
        <div class="flex items-center space-x-3">
          <img
            src="@/assets/images/logo/logo-grupo_2024.png"
            alt="Grupo EMOPS"
            class="h-8 sm:h-9 object-contain"
            onerror="this.style.display='none'"
          />
          <div class="w-px h-5 bg-slate-200 hidden sm:block"></div>
          <span class="text-xs font-semibold text-slate-700 hidden sm:inline tracking-tight">Pesquisa de Satisfação</span>
        </div>

        <!-- Selo de Conexão Segura -->
        <div class="flex items-center space-x-1.5 text-xs text-slate-600 bg-slate-50 px-2.5 py-1 rounded-full border border-slate-200/70">
          <svg class="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
          </svg>
          <span class="font-medium text-3xs sm:text-2xs">Ambiente Seguro</span>
        </div>
      </div>
    </header>

    <!-- Conteúdo Principal: Full-screen no Mobile, Card no Desktop -->
    <main class="flex-grow flex items-center justify-center p-0 sm:p-6 lg:p-8">
      <div class="nps-content-card w-full">
        <!-- ESTADO 1: Carregamento Inicial -->
        <div v-if="loading" class="p-8 sm:p-12 text-center animate-fade-in">
          <div class="nps-spinner-container mx-auto mb-4">
            <div class="nps-spinner"></div>
          </div>
          <h2 class="text-lg font-bold text-slate-800 mb-1">Carregando formulário...</h2>
          <p class="text-sm text-slate-600">Localizando os dados do seu atendimento.</p>
        </div>

        <!-- ESTADO 2: Link Inválido ou Erro -->
        <div v-else-if="erroMensagem" class="animate-fade-in">
          <div class="h-1.5 w-full bg-rose-500"></div>
          <div class="p-6 sm:p-10 text-center">
            <div class="w-16 h-16 mx-auto mb-4 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center shadow-inner">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <h2 class="text-xl font-bold text-slate-800 mb-2">Acesso Indisponível</h2>
            <p class="text-sm text-slate-600 mb-6 leading-relaxed max-w-md mx-auto">{{ erroMensagem }}</p>
            <div class="text-xs text-slate-600 border-t border-slate-100 pt-4">
              Em caso de dúvidas, consulte nossa equipe de atendimento.
            </div>
          </div>
        </div>

        <!-- ESTADO 3: Pesquisa Já Respondida -->
        <div v-else-if="jaRespondido" class="animate-fade-in">
          <div class="h-1.5 w-full bg-emerald-500"></div>
          <div class="p-6 sm:p-10 text-center">
            <div class="w-16 h-16 mx-auto mb-4 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center shadow-inner">
              <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h2 class="text-xl sm:text-2xl font-bold text-slate-800 mb-2">Pesquisa Concluída!</h2>
            <p class="text-sm text-slate-600 mb-4 max-w-md mx-auto leading-relaxed">
              Esta avaliação referente à <strong>OS #{{ osInfo.id_os }}</strong> já foi registrada anteriormente<span v-if="osInfo.respondido_em"> em {{ formatarData(osInfo.respondido_em) }}</span>.
            </p>
            <div class="inline-block bg-slate-50 border border-slate-200/80 rounded-xl px-4 py-3 text-xs text-slate-600 max-w-md">
              Agradecemos sinceramente pela sua colaboração contínua.
            </div>
          </div>
        </div>

        <!-- ESTADO 4: Sucesso Pós-Envio -->
        <div v-else-if="sucessoEnvio" class="animate-fade-in">
          <div class="h-1.5 w-full bg-emerald-500"></div>
          <div class="p-6 sm:p-12 text-center">
            <div class="nps-success-icon-wrap mx-auto mb-4">
              <svg class="w-10 h-10 text-emerald-600" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
            </div>
            <h2 class="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2 tracking-tight">
              Muito Obrigado!
            </h2>
            <p class="text-base text-slate-600 mb-5 max-w-md mx-auto">
              Sua opinião é fundamental para nós, <strong>{{ osInfo.cliente || 'Cliente' }}</strong>.
            </p>

            <!-- Resumo da Avaliação Registrada -->
            <div class="bg-gradient-to-br from-slate-50 to-emerald-50/40 rounded-xl p-4 sm:p-5 mb-6 border border-emerald-100 max-w-md mx-auto text-left">
              <div class="flex items-center justify-between mb-2">
                <span class="text-xs uppercase tracking-wider font-semibold text-slate-600">Sua Avaliação</span>
                <span
                  class="px-2.5 py-0.5 rounded-full text-xs font-bold"
                  :class="badgeSucessoClass"
                >
                  Nota {{ notaSelecionada }} / 10 • {{ categoriaNomeFormatado }}
                </span>
              </div>

              <!-- Tags Selecionadas no Resumo -->
              <div v-if="tagsSelecionadas && tagsSelecionadas.length > 0" class="flex flex-wrap gap-1.5 mb-2.5">
                <span
                  v-for="tag in tagsSelecionadas"
                  :key="'resumo-tag-' + tag"
                  class="inline-block bg-white text-emerald-900 border border-emerald-200/80 rounded-md px-2 py-0.5 text-3xs sm:text-2xs font-medium shadow-3xs"
                >
                  ✓ {{ tag }}
                </span>
              </div>

              <p class="text-xs sm:text-sm text-slate-600 leading-relaxed italic">
                "{{ feedbackMensagemClassificacao }}"
              </p>

              <div v-if="solicitarContato" class="mt-3 pt-2.5 border-t border-emerald-200/60 flex items-center text-xs text-emerald-900 font-medium">
                <svg class="w-4 h-4 mr-1.5 text-emerald-700 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                </svg>
                <span>Nossa coordenação técnica entrará em contato com você pelo WhatsApp.</span>
              </div>
            </div>

            <div class="text-xs text-slate-600 border-t border-slate-100 pt-5">
              <p class="mt-1">Você já pode fechar esta página com segurança.</p>
            </div>
          </div>
        </div>

        <!-- ESTADO 5: Formulário Ativo de Avaliação -->
        <div v-else class="animate-fade-in">
          <!-- Card Header / Perfil do Cliente e Dados da OS -->
          <div class="p-5 sm:p-7 border-b border-slate-100 bg-gradient-to-b from-slate-50/70 to-white">
            <div class="flex items-start sm:items-center space-x-3.5 sm:space-x-4">
              <!-- Avatar Inteligente com Iniciais do Cliente/Empresa -->
              <div class="flex-shrink-0">
                <img
                  v-if="osInfo.cliente_logo"
                  :src="osInfo.cliente_logo"
                  :alt="osInfo.cliente || 'Cliente'"
                  class="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-white shadow-sm ring-2 ring-emerald-100"
                />
                <div
                  v-else
                  class="nps-avatar-initials w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-sm ring-2 ring-emerald-100/80"
                  :style="avatarGradienteStyle"
                  :title="osInfo.cliente || 'Cliente'"
                >
                  {{ clienteIniciais }}
                </div>
              </div>

              <!-- Informações do Cliente & Status -->
              <div class="flex-grow min-w-0">
                <div class="flex flex-wrap items-center justify-between gap-1.5 mb-0.5">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-3xs sm:text-2xs font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200/60">
                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5"></span>
                    Atendimento Concluído
                  </span>
                  <span class="text-xs font-semibold text-slate-600">
                    OS #{{ osInfo.id_os }}
                  </span>
                </div>
                <h1 class="text-lg sm:text-xl font-bold text-slate-900 truncate">
                  Olá, {{ osInfo.cliente || 'Cliente' }}!
                </h1>
                <p class="text-xs sm:text-sm text-slate-600 mt-0.5 leading-snug">
                  Como foi a sua experiência com os nossos serviços?
                </p>
              </div>
            </div>

            <!-- Detalhes do Serviço em Micro-Cards -->
            <div v-if="osInfo.servico || osInfo.data_execucao" class="mt-3.5 pt-3 border-t border-slate-200/60 flex flex-wrap gap-2 text-xs text-slate-600">
              <div v-if="osInfo.servico" class="inline-flex items-center bg-white px-2.5 py-1 rounded-lg border border-slate-200/80 shadow-3xs">
                <svg class="w-3.5 h-3.5 text-slate-600 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                </svg>
                <span><strong>Serviço:</strong> {{ osInfo.servico }}</span>
              </div>
              <div v-if="osInfo.data_execucao" class="inline-flex items-center bg-white px-2.5 py-1 rounded-lg border border-slate-200/80 shadow-3xs">
                <svg class="w-3.5 h-3.5 text-slate-600 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
                <span><strong>Realizado em:</strong> {{ formatarData(osInfo.data_execucao) }}</span>
              </div>
            </div>
          </div>

          <!-- Formulário Interativo -->
          <form @submit.prevent="submeterAvaliacao" class="p-5 sm:p-7">
            <!-- Pergunta Principal da Escala NPS -->
            <div class="mb-4 text-center">
              <h2 class="text-base sm:text-lg font-bold text-slate-900 leading-snug">
                Em uma escala de 0 a 10, o quanto você recomendaria nossos serviços para um amigo ou colega?
              </h2>
              <p class="text-xs text-slate-600 mt-1">
                Toque no mostrador circular para selecionar sua nota:
              </p>
            </div>

            <!-- MOSTRADOR CIRCULAR EM ARCO (Dmitry Sergushkin Arc Dial) -->
            <div class="nps-arc-container relative mx-auto my-2 select-none" style="max-width: 320px;">
              <svg viewBox="0 0 300 215" class="w-full h-auto drop-shadow-sm overflow-visible">
                <defs>
                  <!-- Gradiente do trilho circular (Vermelho -> Amarelo -> Verde) -->
                  <linearGradient id="nps-track-gradient" x1="0%" y1="100%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="#ef4444" />
                    <stop offset="25%" stop-color="#f97316" />
                    <stop offset="55%" stop-color="#f59e0b" />
                    <stop offset="80%" stop-color="#10b981" />
                    <stop offset="100%" stop-color="#059669" />
                  </linearGradient>
                </defs>

                <!-- Trilho colorido interno em arco -->
                <path
                  :d="arcTrackPath"
                  fill="none"
                  stroke="url(#nps-track-gradient)"
                  stroke-width="4.5"
                  stroke-linecap="round"
                  opacity="0.9"
                />

                <!-- 11 Segmentos/Cotas do Arco (0 a 10) -->
                <g v-for="seg in arcSegments" :key="'arc-seg-' + seg.valor" :data-nota="seg.valor" class="nps-arc-segment-group cursor-pointer" @click="selecionarNota(seg.valor)">
                  <!-- Cunha / Segmento de arco -->
                  <path
                    :d="seg.path"
                    :fill="seg.fill"
                    :stroke="seg.stroke"
                    :stroke-width="seg.isSelected ? 2 : 1"
                    class="transition-all duration-200"
                    :class="{ 'nps-seg-elevated': seg.isSelected }"
                  />
                  <!-- Número do segmento -->
                  <text
                    :x="seg.textPos.x"
                    :y="seg.textPos.y"
                    text-anchor="middle"
                    dominant-baseline="central"
                    :fill="seg.textColor"
                    :font-size="seg.fontSize"
                    :font-weight="seg.fontWeight"
                    class="pointer-events-none transition-all duration-150"
                  >
                    {{ seg.valor }}
                  </text>
                </g>

                <!-- Centro do Mostrador: Avatar do Cliente ou Reação Emocional -->
                <g transform="translate(150, 135)">
                  <circle
                    r="33"
                    :fill="centerAvatarBg"
                    stroke="#ffffff"
                    stroke-width="3"
                    class="drop-shadow-sm transition-colors duration-200"
                  />
                  <!-- Emoji reativo ou iniciais -->
                  <text
                    v-if="notaSelecionada !== null"
                    y="2"
                    text-anchor="middle"
                    dominant-baseline="central"
                    font-size="28"
                    class="pointer-events-none select-none"
                  >
                    {{ moodEmoji }}
                  </text>
                  <text
                    v-else
                    y="2"
                    text-anchor="middle"
                    dominant-baseline="central"
                    fill="#ffffff"
                    font-size="17"
                    font-weight="700"
                    class="pointer-events-none select-none"
                  >
                    {{ clienteIniciais }}
                  </text>
                </g>
              </svg>

              <!-- Legendas das extremidades abaixo do mostrador -->
              <div class="flex items-center justify-between text-xs text-slate-600 px-3 -mt-2 font-medium">
                <span class="flex items-center">
                  <span class="w-2 h-2 rounded-full bg-rose-500 mr-1.5"></span>
                  Pouco provável
                </span>
                <span class="flex items-center">
                  Muito provável
                  <span class="w-2 h-2 rounded-full bg-emerald-500 ml-1.5"></span>
                </span>
              </div>
            </div>

            <!-- Mood Banner Suave (Sem Poluição) -->
            <transition name="slide-fade">
              <div
                v-if="notaSelecionada !== null"
                class="mt-3 mb-5 px-4 py-2.5 rounded-xl border flex items-center justify-between transition-all"
                :class="moodBannerClass"
              >
                <div class="flex items-center space-x-2 min-w-0">
                  <span class="text-xl flex-shrink-0 select-none">{{ moodEmoji }}</span>
                  <div class="min-w-0">
                    <p class="font-bold text-xs sm:text-sm tracking-tight truncate" :class="moodTextoClass">
                      {{ moodTitulo }}
                    </p>
                    <p class="text-3xs sm:text-2xs opacity-90 truncate">
                      {{ moodSubtitulo }}
                    </p>
                  </div>
                </div>
                <span
                  class="px-2.5 py-0.5 rounded-full text-xs font-extrabold flex-shrink-0"
                  :class="moodScoreBadgeClass"
                >
                  Nota {{ notaSelecionada }}
                </span>
              </div>
            </transition>

            <!-- SEÇÃO DE CHIPS / TAGS DE MOTIVOS (Moneyview UI Uplift) -->
            <transition name="slide-fade">
              <div v-if="notaSelecionada !== null" class="mb-5 p-4 rounded-xl border bg-slate-50/60 border-slate-200/80">
                <label class="block text-xs sm:text-sm font-bold text-slate-800 mb-1">
                  {{ tituloTagsMotivo }}
                </label>
                <p class="text-3xs sm:text-xs text-slate-600 mb-2.5">
                  Selecione uma ou mais opções que melhor representam o atendimento:
                </p>

                <!-- Tags / Chips Rápidos -->
                <div class="flex flex-wrap gap-1.5 sm:gap-2">
                  <button
                    v-for="tag in listaTagsAtuais"
                    :key="'chip-' + tag"
                    type="button"
                    class="nps-chip px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center space-x-1"
                    :class="[
                      isTagSelecionada(tag)
                        ? chipSelectedClass
                        : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-100/60'
                    ]"
                    @click="alternarTag(tag)"
                  >
                    <span v-if="isTagSelecionada(tag)" class="text-3xs font-bold mr-0.5">✓</span>
                    <span>{{ tag }}</span>
                  </button>
                </div>

                <!-- Fechamento de Loop para Detratores (NPS 2.0) -->
                <div
                  v-if="notaSelecionada <= 6"
                  class="mt-3.5 pt-3 border-t border-rose-200/60 flex items-start space-x-2.5 text-xs text-rose-900"
                >
                  <input
                    id="nps-solicitar-contato"
                    v-model="solicitarContato"
                    type="checkbox"
                    class="mt-0.5 h-4 w-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500 cursor-pointer"
                  />
                  <label for="nps-solicitar-contato" class="cursor-pointer select-none leading-snug">
                    <strong>Deseja que nossa coordenação técnica entre em contato para resolver este ponto?</strong>
                    <span class="block text-3xs text-rose-800/80 mt-0.5">Entraremos em contato prioritário via WhatsApp ou ligação.</span>
                  </label>
                </div>
              </div>
            </transition>

            <!-- Comentário Opcional com Placeholder Contextualizado -->
            <div class="mb-5">
              <label for="nps-comentario" class="block text-sm font-bold text-slate-800 mb-1">
                Comentário ou observação adicional <span class="font-normal text-slate-600 text-xs">(opcional)</span>
              </label>
              <textarea
                id="nps-comentario"
                v-model="comentario"
                maxlength="500"
                rows="3"
                class="nps-textarea w-full p-3.5 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 text-sm text-slate-800 placeholder-slate-400 transition"
                :placeholder="placeholderComentario"
              ></textarea>
              <div class="flex items-center justify-between text-3xs sm:text-xs text-slate-600 mt-1">
                <span>Seu depoimento ajuda nossa diretoria a aperfeiçoar os serviços.</span>
                <span>{{ (comentario || '').length }} / 500</span>
              </div>
            </div>

            <!-- Aviso caso o usuário toque no botão sem nota (Moneyview UI Screen 2) -->
            <transition name="slide-fade">
              <div
                v-if="mostrarAvisoNota && notaSelecionada === null"
                class="mb-3.5 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs font-semibold flex items-center space-x-2"
              >
                <svg class="w-4 h-4 text-rose-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <span>Por favor, selecione uma nota de 0 a 10 no mostrador antes de enviar.</span>
              </div>
            </transition>

            <!-- Botão de Submissão -->
            <div>
              <button
                type="submit"
                :disabled="enviando"
                class="nps-submit-btn w-full py-3.5 px-6 rounded-xl font-bold text-base transition flex items-center justify-center space-x-2"
                :class="[
                  notaSelecionada === null
                    ? 'is-disabled'
                    : 'is-active shadow-md hover:shadow-lg'
                ]"
                @click="aoTentarSubmeter"
              >
                <span v-if="enviando" class="nps-btn-spinner mr-2"></span>
                <span>{{ enviando ? 'Enviando sua avaliação...' : 'Enviar Avaliação' }}</span>
                <svg v-if="!enviando" class="w-4 h-4 ml-1.5 transform transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                </svg>
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>

    <!-- Rodapé Minimalista -->
    <footer class="nps-footer py-4 px-4 sm:px-6 text-center text-xs text-slate-600 border-t border-slate-200/70 bg-white/80">
      <div class="max-w-2xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-1">
        <span>&copy; {{ anoAtual }} Grupo EMOPS. Todos os direitos reservados.</span>
        <span class="text-slate-600 text-3xs sm:text-2xs">Pesquisa de Satisfação de Atendimento</span>
      </div>
    </footer>
  </div>
</template>

<script>
import Connection from '@/functions/Connection.js'
import HOSTS from '@/enviroments.js'
import axios from 'axios'

export default {
  name: 'AvaliacaoNps',
  data () {
    return {
      loading: true,
      enviando: false,
      erroMensagem: null,
      jaRespondido: false,
      sucessoEnvio: false,
      os: null,
      hashKey: null,
      osInfo: {
        id_os: null,
        cliente: '',
        cliente_logo: null,
        servico: '',
        data_execucao: null,
        ja_respondido: false,
        respondido_em: null
      },
      escalaValores: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      notaSelecionada: null,
      tagsSelecionadas: [],
      solicitarContato: false,
      mostrarAvisoNota: false,
      comentario: '',
      motivosDetrator: [
        'Atraso no horário agendado',
        'Pragas retornaram / eficácia',
        'Postura ou atenção do técnico',
        'Falta de orientações pós-serviço',
        'Comunicação ou agendamento',
        'Outro motivo'
      ],
      motivosNeutro: [
        'Cumprimento de horários',
        'Eficácia do tratamento',
        'Comunicação mais ágil',
        'Envio de relatórios técnicos',
        'Preço ou condições',
        'Outra sugestão'
      ],
      motivosPromotor: [
        'Pontualidade exemplar',
        'Técnico educado e cordial',
        'Eliminação eficaz de pragas',
        'Cuidado e segurança no local',
        'Atendimento rápido e eficiente',
        'Outro elogio'
      ]
    }
  },
  computed: {
    anoAtual () {
      return new Date().getFullYear()
    },
    clienteIniciais () {
      const nome = (this.osInfo && this.osInfo.cliente) ? String(this.osInfo.cliente).trim() : ''
      if (!nome) return 'CL'
      const partes = nome.split(/\s+/).filter(Boolean)
      if (partes.length === 1) {
        return partes[0].slice(0, 2).toUpperCase()
      }
      const prim = partes[0].charAt(0)
      const seg = partes[1].charAt(0)
      return (prim + seg).toUpperCase()
    },
    avatarGradienteStyle () {
      const nome = (this.osInfo && this.osInfo.cliente) ? String(this.osInfo.cliente) : 'Cliente'
      let hash = 0
      for (let i = 0; i < nome.length; i++) {
        hash = nome.charCodeAt(i) + ((hash << 5) - hash)
      }
      const gradients = [
        'linear-gradient(135deg, #059669 0%, #0d9488 100%)',
        'linear-gradient(135deg, #0284c7 0%, #2563eb 100%)',
        'linear-gradient(135deg, #0d9488 0%, #0891b2 100%)',
        'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'
      ]
      const index = Math.abs(hash) % gradients.length
      return {
        background: gradients[index]
      }
    },
    centerAvatarBg () {
      if (this.notaSelecionada === null) return '#059669'
      if (this.notaSelecionada >= 9) return '#059669'
      if (this.notaSelecionada >= 7) return '#d97706'
      return '#e11d48'
    },
    arcTrackPath () {
      // Gera arco de trilho interno entre 160° e 380°
      const cx = 150
      const cy = 135
      const r = 72
      const start = this.polarToCartesian(cx, cy, r, 160)
      const end = this.polarToCartesian(cx, cy, r, 380)
      return ['M', start.x, start.y, 'A', r, r, 0, 1, 1, end.x, end.y].join(' ')
    },
    arcSegments () {
      const cx = 150
      const cy = 135
      const totalSpan = 220 // de 160° a 380°
      const step = totalSpan / 11 // 20 graus por fatia
      const segments = []

      for (let i = 0; i <= 10; i++) {
        const isSelected = this.notaSelecionada === i
        const startAng = 160 + i * step + 0.8
        const endAng = 160 + (i + 1) * step - 0.8
        const rInner = isSelected ? 75 : 78
        const rOuter = isSelected ? 122 : 115

        const path = this.describeArcSegment(cx, cy, rInner, rOuter, startAng, endAng)

        // Posição do texto no meio da fatia
        const midAng = (startAng + endAng) / 2
        const rText = isSelected ? 98 : 96.5
        const textPos = this.polarToCartesian(cx, cy, rText, midAng)

        let fill = '#ffffff'
        let stroke = '#e2e8f0'
        let textColor = '#475569'

        if (isSelected) {
          if (i >= 9) {
            fill = '#059669'
            stroke = '#047857'
          } else if (i >= 7) {
            fill = '#d97706'
            stroke = '#b45309'
          } else {
            fill = '#e11d48'
            stroke = '#be123c'
          }
          textColor = '#ffffff'
        } else {
          // Cores suaves de fundo por categoria
          if (i >= 9) {
            fill = '#f0fdf4'
            stroke = '#bbf7d0'
            textColor = '#166534'
          } else if (i >= 7) {
            fill = '#fffbeb'
            stroke = '#fde68a'
            textColor = '#854d0e'
          } else {
            fill = '#fff1f2'
            stroke = '#fecdd3'
            textColor = '#9f1239'
          }
        }

        segments.push({
          valor: i,
          isSelected,
          path,
          textPos,
          fill,
          stroke,
          textColor,
          fontSize: isSelected ? 15 : 13,
          fontWeight: isSelected ? '800' : '700'
        })
      }
      return segments
    },
    categoriaNomeFormatado () {
      if (this.notaSelecionada === null) return ''
      if (this.notaSelecionada >= 9) return 'Promotor'
      if (this.notaSelecionada >= 7) return 'Neutro'
      return 'Detrator'
    },
    moodEmoji () {
      if (this.notaSelecionada === null) return '✨'
      if (this.notaSelecionada >= 9) return '😍'
      if (this.notaSelecionada >= 7) return '😐'
      return '😔'
    },
    moodTitulo () {
      if (this.notaSelecionada === null) return 'Sua opinião é muito importante!'
      if (this.notaSelecionada >= 9) return 'Super satisfeito / Recomendo com certeza!'
      if (this.notaSelecionada >= 7) return 'Atendimento bom / Pode melhorar'
      return 'Insatisfeito / Não recomendaria'
    },
    moodSubtitulo () {
      if (this.notaSelecionada === null) return 'Ajude o Grupo EMOPS a elevar o padrão de atendimento.'
      if (this.notaSelecionada >= 9) return 'Que alegria saber que você adorou o serviço prestado!'
      if (this.notaSelecionada >= 7) return 'Diga-nos o que falta para alcançarmos a nota 10.'
      return 'Sentimos muito por isso. Queremos entender e corrigir de imediato.'
    },
    moodBannerClass () {
      if (this.notaSelecionada === null) return 'bg-slate-50/80 border-slate-200/80'
      if (this.notaSelecionada >= 9) return 'bg-emerald-50 border-emerald-200'
      if (this.notaSelecionada >= 7) return 'bg-amber-50 border-amber-200'
      return 'bg-rose-50 border-rose-200'
    },
    moodTextoClass () {
      if (this.notaSelecionada === null) return 'text-slate-800'
      if (this.notaSelecionada >= 9) return 'text-emerald-900'
      if (this.notaSelecionada >= 7) return 'text-amber-900'
      return 'text-rose-900'
    },
    moodScoreBadgeClass () {
      if (this.notaSelecionada === null) return ''
      if (this.notaSelecionada >= 9) return 'bg-emerald-600 text-white'
      if (this.notaSelecionada >= 7) return 'bg-amber-500 text-white'
      return 'bg-rose-600 text-white'
    },
    tituloTagsMotivo () {
      if (this.notaSelecionada === null) return 'O que motivou sua nota?'
      if (this.notaSelecionada >= 9) return 'O que você mais gostou no atendimento?'
      if (this.notaSelecionada >= 7) return 'O que podemos aprimorar em nossos serviços?'
      return 'Qual o principal motivo da sua insatisfação?'
    },
    listaTagsAtuais () {
      if (this.notaSelecionada === null) return []
      if (this.notaSelecionada >= 9) return this.motivosPromotor
      if (this.notaSelecionada >= 7) return this.motivosNeutro
      return this.motivosDetrator
    },
    chipSelectedClass () {
      if (this.notaSelecionada >= 9) return 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
      if (this.notaSelecionada >= 7) return 'bg-amber-500 text-white border-amber-500 shadow-sm'
      return 'bg-rose-600 text-white border-rose-600 shadow-sm'
    },
    badgeSucessoClass () {
      if (this.notaSelecionada === null) return ''
      if (this.notaSelecionada >= 9) {
        return 'bg-emerald-100 text-emerald-800'
      }
      if (this.notaSelecionada >= 7) {
        return 'bg-amber-100 text-amber-800'
      }
      return 'bg-rose-100 text-rose-800'
    },
    feedbackMensagemClassificacao () {
      if (this.notaSelecionada === null) return ''
      if (this.notaSelecionada >= 9) {
        return 'Ficamos muito felizes em saber que superamos suas expectativas! Seu reconhecimento motiva toda a nossa equipe técnica.'
      }
      if (this.notaSelecionada >= 7) {
        return 'Agradecemos o seu feedback! Trabalhamos para que cada detalhe seja impecável e queremos conquistar a sua nota 10.'
      }
      return 'Sentimos muito por não termos atendido plenamente o seu padrão. Seu relato é essencial para corrigirmos qualquer falha imediatamente.'
    },
    placeholderComentario () {
      if (this.notaSelecionada === null) {
        return 'Conte-nos o que motivou a sua avaliação...'
      }
      if (this.notaSelecionada >= 9) {
        return 'Deseja deixar um elogio nominal para o técnico ou observação? (Opcional)'
      }
      if (this.notaSelecionada >= 7) {
        return 'O que poderíamos ter feito de diferente para merecer uma nota 10? (Opcional)'
      }
      return 'Descreva com suas palavras o que aconteceu para que nossa diretoria possa solucionar. (Opcional)'
    }
  },
  created () {
    this.obterParametrosEBuscarInfo()
  },
  mounted () {
    this.injetarTailwindCdn()
  },
  methods: {
    polarToCartesian (cx, cy, r, angleInDegrees) {
      const angleInRadians = (angleInDegrees * Math.PI) / 180.0
      return {
        x: Math.round((cx + r * Math.cos(angleInRadians)) * 100) / 100,
        y: Math.round((cy + r * Math.sin(angleInRadians)) * 100) / 100
      }
    },
    describeArcSegment (cx, cy, rInner, rOuter, startAngle, endAngle) {
      const p1 = this.polarToCartesian(cx, cy, rOuter, startAngle)
      const p2 = this.polarToCartesian(cx, cy, rOuter, endAngle)
      const p3 = this.polarToCartesian(cx, cy, rInner, endAngle)
      const p4 = this.polarToCartesian(cx, cy, rInner, startAngle)
      const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'
      return [
        'M', p1.x, p1.y,
        'A', rOuter, rOuter, 0, largeArcFlag, 1, p2.x, p2.y,
        'L', p3.x, p3.y,
        'A', rInner, rInner, 0, largeArcFlag, 0, p4.x, p4.y,
        'Z'
      ].join(' ')
    },
    injetarTailwindCdn () {
      if (typeof document !== 'undefined' && !document.getElementById('tailwind-cdn-nps')) {
        const script = document.createElement('script')
        script.id = 'tailwind-cdn-nps'
        script.src = 'https://cdn.tailwindcss.com'
        document.head.appendChild(script)
      }
    },
    obterParametrosEBuscarInfo () {
      this.os = this.$route && this.$route.query ? (this.$route.query.os || null) : null
      this.hashKey = this.$route && this.$route.query ? (this.$route.query.hashKey || null) : null

      if (!this.os || !this.hashKey) {
        this.loading = false
        this.erroMensagem = 'Link de pesquisa incompleto ou inválido. Abra o endereço completo enviado pela nossa equipe.'
        return
      }

      this.buscarInformacoesFormulario()
    },
    async buscarInformacoesFormulario () {
      this.loading = true
      this.erroMensagem = null

      try {
        let res = null
        try {
          res = await Connection.get('nps/formulario-info', {
            params: { os: this.os, hashKey: this.hashKey }
          })
        } catch (innerErr) {
          const url = (HOSTS.API || '').replace(/\/+$/, '') + '/nps/formulario-info'
          const resp = await axios.get(url, {
            params: { os: this.os, hashKey: this.hashKey }
          })
          res = resp.data
        }

        if (res && res.success && res.info) {
          this.osInfo = res.info
          if (res.info.ja_respondido) {
            this.jaRespondido = true
          }
        } else {
          this.erroMensagem = (res && res.message) || 'Ordem de serviço não encontrada ou link de pesquisa expirado.'
        }
      } catch (err) {
        console.error('[NPS Formulario] Erro ao carregar info:', err)
        if (err.response && err.response.data && err.response.data.message) {
          this.erroMensagem = err.response.data.message
        } else {
          this.erroMensagem = 'Não foi possível carregar os dados desta pesquisa no momento. Por favor, tente novamente mais tarde.'
        }
      } finally {
        this.loading = false
      }
    },
    selecionarNota (valor) {
      this.notaSelecionada = valor
      this.tagsSelecionadas = []
      this.mostrarAvisoNota = false
    },
    alternarTag (tag) {
      const idx = this.tagsSelecionadas.indexOf(tag)
      if (idx > -1) {
        this.tagsSelecionadas.splice(idx, 1)
      } else {
        this.tagsSelecionadas.push(tag)
      }
    },
    isTagSelecionada (tag) {
      return this.tagsSelecionadas.indexOf(tag) > -1
    },
    getCategoriaNota (valor) {
      if (valor >= 9) return 'promotor'
      if (valor >= 7) return 'neutro'
      return 'detrator'
    },
    formatarData (dataStr) {
      if (!dataStr) return ''
      try {
        const d = new Date(dataStr)
        if (isNaN(d.getTime())) return dataStr
        return d.toLocaleDateString('pt-BR')
      } catch (e) {
        return dataStr
      }
    },
    aoTentarSubmeter () {
      if (this.notaSelecionada === null) {
        this.mostrarAvisoNota = true
      }
    },
    async submeterAvaliacao () {
      if (this.notaSelecionada === null) {
        this.mostrarAvisoNota = true
        return
      }
      if (this.enviando) return

      this.enviando = true
      try {
        let comentarioFormatado = this.comentario ? this.comentario.trim() : ''
        if (this.tagsSelecionadas && this.tagsSelecionadas.length > 0) {
          const tagsStr = `[Motivos: ${this.tagsSelecionadas.join(', ')}]`
          comentarioFormatado = comentarioFormatado ? `${tagsStr} ${comentarioFormatado}` : tagsStr
        }
        if (this.solicitarContato) {
          const solicitacaoStr = '[Cliente solicitou retorno da coordenação técnica]'
          comentarioFormatado = comentarioFormatado ? `${comentarioFormatado} ${solicitacaoStr}` : solicitacaoStr
        }

        const payload = {
          os: this.os,
          hashKey: this.hashKey,
          nota: this.notaSelecionada,
          tags: this.tagsSelecionadas,
          solicitar_contato: this.solicitarContato,
          comentario: comentarioFormatado || null
        }

        let res = null
        try {
          res = await Connection.post('nps/submeter-avaliacao', payload)
        } catch (innerErr) {
          const url = (HOSTS.API || '').replace(/\/+$/, '') + '/nps/submeter-avaliacao'
          const resp = await axios.post(url, payload)
          res = resp.data
        }

        if (res && res.success) {
          this.sucessoEnvio = true
        } else {
          const msg = (res && res.message) || 'Erro ao registrar sua avaliação.'
          alert(msg)
        }
      } catch (err) {
        console.error('[NPS Formulario] Erro ao submeter:', err)
        const msg = (err.response && err.response.data && err.response.data.message) || 'Erro ao enviar avaliação. Por favor, tente novamente.'
        alert(msg)
      } finally {
        this.enviando = false
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

/* Resets e Tipografia Moderna */
.nps-app-root {
  background-color: #f8fafc;
  background-image: radial-gradient(#cbd5e1 0.75px, transparent 0.75px), radial-gradient(#cbd5e1 0.75px, #f8fafc 0.75px);
  background-size: 30px 30px;
  background-position: 0 0, 15px 15px;
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: #0f172a;
  -webkit-font-smoothing: antialiased;
}

.text-2xs {
  font-size: 0.7rem;
  line-height: 0.95rem;
}

.text-3xs {
  font-size: 0.65rem;
  line-height: 0.85rem;
}

.shadow-3xs {
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
}

/* Card Principal: Full-width no Mobile, Card com Bordas e Sombra no Desktop */
.nps-content-card {
  width: 100%;
  background-color: #ffffff;
  overflow: hidden;
}

@media (min-width: 640px) {
  .nps-content-card {
    max-width: 38rem; /* 608px */
    border-radius: 1.5rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 20px 40px -15px rgba(15, 23, 42, 0.08), 0 0 1px 1px rgba(15, 23, 42, 0.02);
    margin: 1.5rem auto;
    overflow: hidden;
  }
}

/* Spinner de Carregamento */
.nps-spinner {
  width: 44px;
  height: 44px;
  border: 3px solid #e2e8f0;
  border-top-color: #059669;
  border-radius: 50%;
  animation: nps-spin 0.8s cubic-bezier(0.6, 0.2, 0.4, 0.8) infinite;
}

.nps-btn-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: nps-spin 0.8s linear infinite;
}

@keyframes nps-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Arco Interativo (Dmitry Sergushkin Arc Dial) */
.nps-arc-segment-group:hover path:not(.nps-seg-elevated) {
  filter: brightness(0.95);
  transform: scale(1.01);
  transform-origin: 150px 135px;
}

.nps-seg-elevated {
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.18));
}

/* Chips / Tags de Motivo */
.nps-chip {
  cursor: pointer;
  user-select: none;
  font-size: 0.78rem;
}
.nps-chip:active {
  transform: scale(0.97);
}

/* Botão de Envio */
.nps-submit-btn {
  background: linear-gradient(135deg, #059669 0%, #0d9488 100%);
  color: #ffffff;
  cursor: pointer;
  box-shadow: 0 4px 14px 0 rgba(5, 150, 105, 0.35);
}

.nps-submit-btn:hover:not(.is-disabled) {
  background: linear-gradient(135deg, #047857 0%, #0f766e 100%);
  transform: translateY(-1px);
  box-shadow: 0 6px 18px 0 rgba(5, 150, 105, 0.42);
}

.nps-submit-btn:active:not(.is-disabled) {
  transform: translateY(0);
}

.nps-submit-btn.is-disabled {
  background: #cbd5e1;
  color: #64748b;
  cursor: pointer;
  box-shadow: none;
}

/* Tela de Sucesso */
.nps-success-icon-wrap {
  width: 76px;
  height: 76px;
  background-color: #ecfdf5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 10px #f0fdf4;
  animation: pulse-ring 2s infinite cubic-bezier(0.4, 0, 0.6, 1);
}

@keyframes pulse-ring {
  0%, 100% {
    box-shadow: 0 0 0 8px #f0fdf4;
  }
  50% {
    box-shadow: 0 0 0 14px #dcfce7;
  }
}

/* Transições e Animações */
.animate-fade-in {
  animation: npsFadeIn 0.35s ease-out;
}

@keyframes npsFadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.slide-fade-enter-active {
  transition: all 0.25s ease-out;
}
.slide-fade-leave-active {
  transition: all 0.15s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateY(6px);
  opacity: 0;
}
</style>
